/**
 * Queue message contract v1 (§132) and per-job-type payloads (`integration_jobs.payload_json`).
 *
 * Queue messages carry IDs only – never PII, codes or tokens. The consumer loads the job and the
 * entity from D1 (D1 is the source of truth, §136) and must be idempotent (§133).
 *
 * Secrets in emails (verification token, magic link) are NEVER persisted in plaintext, neither in
 * D1 nor in queue messages: the email job derives the token at send time from its idempotency key
 * (HMAC with SESSION_SECRET, deterministic per job – ADR 049, CR-WS4-1), stores only its hash and
 * sends the link (ADR 016, „mint at send time“).
 */
import { z } from 'zod';
import {
  EntityType,
  IntegrationJobType,
  IntegrationJobTypeSchema,
  ProvisioningTriggerSchema,
  VerificationEmailReasonSchema,
} from './enums';

export const QUEUE_MESSAGE_VERSION = 1;

export const QueueMessageV1Schema = z
  .object({
    version: z.literal(QUEUE_MESSAGE_VERSION),
    type: IntegrationJobTypeSchema,
    jobId: z.uuid(),
    /** ID of the entity named by JOB_ENTITY_TYPE[type]. */
    entityId: z.uuid(),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type QueueMessageV1 = z.infer<typeof QueueMessageV1Schema>;
/** Union of all supported message versions (only v1 today). */
export const QueueMessageSchema = QueueMessageV1Schema;
export type QueueMessage = QueueMessageV1;

/** Which entity `integration_jobs.entity_id` / `QueueMessage.entityId` refers to. */
export const JOB_ENTITY_TYPE = {
  [IntegrationJobType.GENERATE_BATCH_CODES]: EntityType.BATCH,
  [IntegrationJobType.PROVISION_LEARNINGSUITE]: EntityType.REGISTRATION,
  [IntegrationJobType.SEND_VERIFICATION_EMAIL]: EntityType.REGISTRATION,
  [IntegrationJobType.SEND_ADMIN_LOGIN_EMAIL]: EntityType.ADMIN_USER,
  [IntegrationJobType.RENDER_BATCH_PDF]: EntityType.GENERATED_DOCUMENT,
  [IntegrationJobType.SYNC_BATCH_SHEET]: EntityType.BATCH,
} as const satisfies Record<IntegrationJobType, EntityType>;

// --- payload_json per job type ------------------------------------------------------------------

export const GenerateBatchCodesPayloadSchema = z
  .object({
    /** 0-based chunk index. */
    chunkIndex: z.number().int().nonnegative(),
    /** Number of codes this chunk must create (last chunk may be smaller). */
    chunkSize: z.number().int().positive(),
    /** Total chunk count of the batch (last chunk sets READY). */
    chunkCount: z.number().int().positive(),
  })
  .strict();

export const ProvisionLearningSuitePayloadSchema = z
  .object({ trigger: ProvisioningTriggerSchema })
  .strict();

export const SendVerificationEmailPayloadSchema = z
  .object({ reason: VerificationEmailReasonSchema })
  .strict();

export const SendAdminLoginEmailPayloadSchema = z
  .object({
    /** SHA-256 of the requesting IP (hex), stored on the minted login token; null if unknown. */
    requestedIpHash: z.string().nullable(),
  })
  .strict();

/**
 * RENDER_BATCH_PDF renders ANY generated document (PDF, ZIP, CSV) – the handler dispatches on
 * `generated_documents.type`. `part` is only used by BATCH_CSV (ADR 066): 0-based part index of
 * CSV_CODES_PER_PART codes; absent = part 0.
 */
export const RenderBatchPdfPayloadSchema = z
  .object({ part: z.number().int().nonnegative().optional() })
  .strict();

export const SyncBatchSheetPayloadSchema = z
  .object({
    /** Start of the debounce window (epoch ms), part of the idempotency key. */
    windowStart: z.number().int().nonnegative(),
    /**
     * 1-based part of the sync chain (CR-WS1-2, ADR 067). Missing (jobs created before the
     * change) = 1. Part n+1 is created only by the SUCCEEDED callback of part n.
     */
    part: z.number().int().positive().default(1),
    /** Total parts, fixed by part 1 from the code count; absent for part 1 (computed then). */
    partCount: z.number().int().positive().optional(),
  })
  .strict();

export const JOB_PAYLOAD_SCHEMAS = {
  [IntegrationJobType.GENERATE_BATCH_CODES]: GenerateBatchCodesPayloadSchema,
  [IntegrationJobType.PROVISION_LEARNINGSUITE]: ProvisionLearningSuitePayloadSchema,
  [IntegrationJobType.SEND_VERIFICATION_EMAIL]: SendVerificationEmailPayloadSchema,
  [IntegrationJobType.SEND_ADMIN_LOGIN_EMAIL]: SendAdminLoginEmailPayloadSchema,
  [IntegrationJobType.RENDER_BATCH_PDF]: RenderBatchPdfPayloadSchema,
  [IntegrationJobType.SYNC_BATCH_SHEET]: SyncBatchSheetPayloadSchema,
} as const satisfies Record<IntegrationJobType, z.ZodType>;

export type JobPayload<T extends IntegrationJobType> = z.infer<(typeof JOB_PAYLOAD_SCHEMAS)[T]>;

// --- idempotency keys (§25) ---------------------------------------------------------------------

/**
 * Builders for `integration_jobs.idempotency_key` (UNIQUE). The key identifies the unit of work;
 * inserting a second job with the same key fails → duplicate work is impossible.
 * The provisioning key is also sent to external APIs where supported.
 */
export const IdempotencyKeys = {
  /** §25 – first provisioning job after verification. */
  provision: (registrationId: string) => `registration:${registrationId}:provision`,
  /** §98 – admin retry creates a NEW job; `n` = 1-based admin retry counter. */
  provisionAdminRetry: (registrationId: string, n: number) =>
    `registration:${registrationId}:provision:retry:${n}`,
  /** §108 – reconciliation reschedule; `n` = admin_retry_count + 1 at scheduling time. */
  provisionReconcile: (registrationId: string, n: number) =>
    `registration:${registrationId}:provision:reconcile:${n}`,
  /** `n` = 1-based counter of verification emails of this registration. */
  verificationEmail: (registrationId: string, n: number) =>
    `registration:${registrationId}:verification-email:${n}`,
  /** One job per login token request (`requestId` of the HTTP request). */
  adminLoginEmail: (adminUserId: string, requestId: string) =>
    `admin:${adminUserId}:login-email:${requestId}`,
  generateBatchChunk: (batchId: string, chunkIndex: number) =>
    `batch:${batchId}:chunk:${chunkIndex}`,
  renderDocument: (documentId: string) => `document:${documentId}:render`,
  /** BATCH_CSV part n ≥ 1 (part 0 uses `renderDocument`), ADR 066. */
  renderDocumentPart: (documentId: string, part: number) => `document:${documentId}:part:${part}`,
  /** Debounced: one sync per batch and window (SHEET_SYNC_DEBOUNCE_MS). */
  syncBatchSheet: (batchId: string, windowStart: number) =>
    `batch:${batchId}:sheet-sync:${windowStart}`,
  /** Part n ≥ 2 of the sync chain of one window (CR-WS1-2, ADR 067); part 1 = syncBatchSheet. */
  syncBatchSheetPart: (batchId: string, windowStart: number, part: number) =>
    `batch:${batchId}:sheet-sync:${windowStart}:part:${part}`,
} as const;
